import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useLang, LangToggle } from "../lib/i18n";
import { LogOut, Menu, X, Eye } from "lucide-react";
import { LogoMark } from "../lib/brand";
import { api } from "../lib/api";

export const Shell = ({ items, brand, subtitle, children, testid }) => {
  const { user, logout } = useAuth();
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const stopImpersonation = async () => {
    try {
      await api.post("/auth/stop-impersonation");
    } finally {
      window.location.href = "/platform/tenants";
    }
  };

  const sidebar = (
    <div className="flex flex-col h-full bg-brand-dark text-emerald-50/90">
      <div className="px-5 py-5 border-b border-white/10 flex items-center gap-3">
        <LogoMark className="w-9 h-9" />
        <div className="min-w-0">
          <div className="font-display font-bold text-white text-sm truncate" data-testid="shell-brand">{brand}</div>
          {subtitle && <div className="text-[11px] text-gold/80 uppercase tracking-[0.15em] truncate">{subtitle}</div>}
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
        {items.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            data-testid={item.testid}
            onClick={() => setOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors border-l-2 ${
                isActive
                  ? "bg-white/10 text-white border-gold"
                  : "text-emerald-100/70 border-transparent hover:bg-white/5 hover:text-white"
              }`
            }
          >
            <item.icon className="w-4 h-4 shrink-0" />
            <span className="truncate">{item.label}</span>
            {item.badge > 0 && (
              <span className="ml-auto bg-gold text-brand-dark text-[10px] font-bold rounded-full px-2 py-0.5">{item.badge}</span>
            )}
          </NavLink>
        ))}
      </nav>
      <div className="p-4 border-t border-white/10">
        <div className="text-xs text-emerald-100/60 truncate mb-2">{user?.email}</div>
        <button
          data-testid="logout-btn"
          onClick={logout}
          className="flex items-center gap-2 text-xs text-emerald-100/80 hover:text-gold transition-colors"
        >
          <LogOut className="w-3.5 h-3.5" /> {t("common.logout")}
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background" data-testid={testid}>
      <div className="hidden lg:fixed lg:inset-y-0 lg:left-0 lg:w-64 lg:block">{sidebar}</div>
      {open && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-64 z-50">{sidebar}</div>
        </div>
      )}
      <div className="lg:pl-64 flex flex-col min-h-screen">
        {user?.impersonated && (
          <div data-testid="impersonation-banner" className="bg-amber-400 text-amber-950 px-4 py-2 text-xs font-semibold flex flex-wrap items-center justify-center gap-3">
            <Eye className="w-3.5 h-3.5" /> Anda sedang melihat portal sebagai pentadbir koperasi ini (impersonation)
            <button data-testid="stop-impersonation-btn" onClick={stopImpersonation} className="underline font-bold hover:text-amber-800 transition-colors">
              Kembali ke Platform Admin
            </button>
          </div>
        )}
        <header className="sticky top-0 z-30 bg-white/85 backdrop-blur-xl border-b border-brand-deep/10 px-4 sm:px-6 py-3 flex items-center gap-3">
          <button data-testid="mobile-menu-btn" className="lg:hidden p-2 -ml-2 text-brand-deep" onClick={() => setOpen(!open)}>
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
          <div className="flex-1" />
          <LangToggle />
          <div className="hidden sm:flex items-center gap-2 pl-3 border-l border-brand-deep/10">
            <div className="w-8 h-8 rounded-full bg-brand-deep text-gold-light flex items-center justify-center text-xs font-bold">
              {(user?.name || "U").slice(0, 1).toUpperCase()}
            </div>
            <span className="text-sm font-medium text-brand-dark max-w-[140px] truncate">{user?.name}</span>
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-6 lg:p-8">{children}</main>
      </div>
    </div>
  );
};

export const StatCard = ({ label, value, icon: Icon, testid, sub }) => (
  <div data-testid={testid} className="bg-card rounded-xl border border-brand-deep/10 p-5 hover:shadow-md transition-shadow">
    <div className="flex items-start justify-between">
      <div>
        <div className="text-xs font-medium uppercase tracking-[0.15em] text-muted-foreground">{label}</div>
        <div className="mt-2 font-display text-2xl font-bold text-brand-dark">{value}</div>
        {sub && <div className="mt-1 text-xs text-muted-foreground">{sub}</div>}
      </div>
      {Icon && (
        <div className="w-10 h-10 rounded-lg bg-brand-light flex items-center justify-center">
          <Icon className="w-5 h-5 text-brand-deep" />
        </div>
      )}
    </div>
  </div>
);

export const StatusBadge = ({ status }) => {
  const map = {
    active: "bg-emerald-100 text-emerald-800", paid: "bg-emerald-100 text-emerald-800",
    approved: "bg-emerald-100 text-emerald-800", verified: "bg-emerald-100 text-emerald-800",
    trial: "bg-amber-100 text-amber-800", pending: "bg-amber-100 text-amber-800", unpaid: "bg-amber-100 text-amber-800",
    suspended: "bg-red-100 text-red-800", rejected: "bg-red-100 text-red-800", inactive: "bg-slate-100 text-slate-700",
    trialing: "bg-amber-100 text-amber-800",
  };
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold uppercase tracking-wide ${map[status] || "bg-slate-100 text-slate-700"}`}>
      {status}
    </span>
  );
};

export const PageTitle = ({ title, sub, children }) => (
  <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
    <div>
      <h1 className="font-display text-2xl font-bold text-brand-dark">{title}</h1>
      {sub && <p className="text-sm text-muted-foreground mt-1">{sub}</p>}
    </div>
    {children}
  </div>
);

export const Spinner = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="w-10 h-10 border-4 border-brand-deep/20 border-t-brand-deep rounded-full animate-spin" />
  </div>
);
